/**
 * Map building blocks shared by the main map (MapPage) and the spot picker
 * of «Играть» (SpotMapPicker). Moved out of MapPage unchanged.
 */
import { useEffect, useState, type MutableRefObject } from "react";
import { CircleMarker, TileLayer } from "react-leaflet";
import { Info, Search, X } from "lucide-react";

import { Badge } from "@/ui";
import { getLocationManager } from "@/lib/telegram";
import { noAutofill } from "@/lib/noAutofill";
import { MAP_ROUND_BUTTON, type SearchableSpot } from "./mapUtils";
import type { UserPos } from "./useUserLocation";

// Stadia Alidade Smooth, proxied through our Nginx so the basemap works in
// clients that strip the Referer header (Telegram WebViews, privacy browsers)
// — Stadia's domain auth would otherwise return 401 and leave the map grey.
// {r} expands to "@2x" on retina; Stadia's free tier is generous enough to
// keep it on.
const STADIA_TILES_LIGHT = "/tiles/alidade_smooth/{z}/{x}/{y}{r}.png";
const STADIA_TILES_DARK = "/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png";

// Mirrors the `data-theme` attribute Telegram/app sets on <html>. We swap
// Stadia's light/dark tile URL based on it so the basemap matches the shell.
function useIsDarkTheme(): boolean {
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const root = document.documentElement;
    const update = () => setDark(root.getAttribute("data-theme") === "dark");
    const obs = new MutationObserver(update);
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/** Stadia basemap that follows the light/dark theme. */
export function BaseTiles() {
  const isDark = useIsDarkTheme();
  return (
    <TileLayer
      key={isDark ? "dark" : "light"}
      url={isDark ? STADIA_TILES_DARK : STADIA_TILES_LIGHT}
      maxZoom={20}
    />
  );
}

/** Blue dot + accuracy halo for the user's position. */
export function UserPositionMarker({ pos }: { pos: UserPos }) {
  return (
    <>
      <CircleMarker
        center={[pos.lat, pos.lng]}
        radius={8}
        pathOptions={{
          color: "#fff",
          weight: 2,
          fillColor: "#2481CC",
          fillOpacity: 1,
        }}
      />
      <CircleMarker
        center={[pos.lat, pos.lng]}
        radius={Math.max(12, pos.accuracy / 10)}
        pathOptions={{
          stroke: false,
          fillColor: "#2481CC",
          fillOpacity: 0.15,
        }}
      />
    </>
  );
}

export function LocateButton({
  locating,
  fixStale,
  locateError,
  onClick,
  style,
}: {
  locating: boolean;
  fixStale: boolean;
  locateError: string | null;
  onClick: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        fixStale
          ? "Обновить местоположение"
          : locating
            ? "Показать меня на карте"
            : "Найти меня"
      }
      title={
        locateError ??
        (fixStale
          ? "Координаты не обновляются — тапните, чтобы обновить"
          : locating
            ? "Показать меня"
            : "Найти меня")
      }
      style={{
        ...MAP_ROUND_BUTTON,
        color: fixStale ? "var(--brand-orange)" : locating ? "#2481CC" : "var(--fg1)",
        ...style,
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
      </svg>
    </button>
  );
}

export function LocateErrorToast({
  message,
  onDismiss,
  style,
}: {
  message: string;
  onDismiss: () => void;
  style?: React.CSSProperties;
}) {
  // Offer TG's settings sheet only once TG has actually asked for access.
  const onOpenSettings =
    getLocationManager()?.isAccessRequested === true
      ? () => getLocationManager()?.openSettings()
      : undefined;
  return (
    <div
      role="status"
      style={{
        position: "absolute",
        left: 16,
        right: 16,
        bottom: `calc(80px + var(--safe-bottom))`,
        zIndex: 1100,
        background: "var(--bg-section)",
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        ...style,
      }}
    >
      <span
        style={{
          flex: 1,
          fontFamily: "var(--font-body)",
          fontSize: 14,
          color: "var(--fg1)",
        }}
      >
        {message}
      </span>
      {onOpenSettings && (
        <button
          type="button"
          onClick={onOpenSettings}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--brand-orange)",
            fontFamily: "var(--font-body)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Настройки
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Закрыть"
        style={{
          background: "transparent",
          border: 0,
          color: "var(--fg2)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

export function SpotSearchBox<T extends SearchableSpot>({
  inputRef,
  value,
  onChange,
  onClose,
  results,
  onPick,
  isLive,
}: {
  inputRef: MutableRefObject<HTMLInputElement | null>;
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
  results: T[];
  onPick: (spot: T) => void;
  isLive?: (id: number) => boolean;
}) {
  return (
    <div
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
        }}
      >
        <Search size={18} color="var(--fg3)" />
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
            if (e.key === "Enter" && results[0]) onPick(results[0]);
          }}
          placeholder="Найти по названию или адресу"
          {...noAutofill}
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            background: "transparent",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            color: "var(--fg1)",
            minWidth: 0,
          }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть поиск"
          style={{
            background: "transparent",
            border: 0,
            color: "var(--fg2)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            padding: 4,
          }}
        >
          <X size={18} />
        </button>
      </div>
      {value.trim() && (
        <div
          style={{
            borderTop: "1px solid var(--separator)",
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          {results.length === 0 ? (
            <div
              style={{
                padding: "16px 12px",
                color: "var(--fg2)",
                fontSize: 14,
                textAlign: "center",
              }}
            >
              Ничего не нашли.
            </div>
          ) : (
            results.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onPick(s)}
                style={{
                  width: "100%",
                  background: "transparent",
                  border: 0,
                  borderTop: "1px solid var(--separator)",
                  textAlign: "left",
                  padding: "10px 12px",
                  cursor: "pointer",
                  color: "var(--fg1)",
                  fontFamily: "var(--font-body)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {s.name}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--fg3)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginTop: 2,
                    }}
                  >
                    {s.address}
                  </div>
                </div>
                {isLive?.(s.id) && <Badge kind="live">Live</Badge>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Minimal attribution chip: a small (i) button in the bottom-left of the map
// that, when tapped, expands into a pill with the Stadia/OMT/OSM links. Keeps
// the map visually clean while still meeting Stadia's + OSM's attribution
// requirements. Tapping elsewhere on the map collapses it back.
export function AttributionBadge() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 1099,
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          left: 8,
          bottom: `calc(8px + var(--safe-bottom))`,
          zIndex: 1100,
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Атрибуции карты"
          aria-expanded={open}
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            border: 0,
            background: "rgba(0,0,0,0.35)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <Info size={14} />
        </button>
        {open && (
          <div
            style={{
              marginLeft: 6,
              background: "var(--bg-section)",
              color: "var(--fg2)",
              border: "1px solid var(--separator)",
              borderRadius: 999,
              padding: "4px 10px",
              fontFamily: "var(--font-body)",
              fontSize: 11,
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            }}
          >
            ©{" "}
            <a href="https://stadiamaps.com/" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
              Stadia Maps
            </a>
            {" © "}
            <a href="https://openmaptiles.org/" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
              OpenMapTiles
            </a>
            {" © "}
            <a href="https://openstreetmap.org/copyright" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>
              OSM
            </a>
          </div>
        )}
      </div>
    </>
  );
}
